import React, { useEffect, useState } from 'react';
import PageHeader from '../common/PageHeader/PageHeader';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';
import Button from '../common/Button/Button';
import SlackModal from '../modals/SlackModal/SlackModal';
import { apiClient } from '../../services/apiClient';
import { toastService } from '../../services/toastService';
import { useAppContext } from '../../context/AppContext';
import './AgentsManagement.css';

const SLACK_AGENT_ID = 'slack-bot';
const EMAIL_AGENT_ID = 'email-context';
const GOOGLE_CALENDAR_AGENT_ID = 'google-calendar';
const SHARED_GOOGLE_AGENT_IDS = [EMAIL_AGENT_ID, GOOGLE_CALENDAR_AGENT_ID];
const DEFAULT_SLACK_STATUS = {
  api_authenticated: false,
  enabled: false,
  handler_running: false,
  startup_enabled: null,
  error: null
};

const DEFAULT_AGENTS = [
  {
    id: 'weather-forecast',
    name: 'Weather Forecast',
    description: "Checks the user's location and retrieves a 48-hour weather forecast, presenting it directly in the conversation.",
    icon: 'fas fa-cloud-sun',
    color: '#38bdf8',
    provider: 'n8n',
    category: 'Prompt Agent',
    triggerLabel: 'Runs during chat prompts',
    runtimeLabel: 'Webhook workflow',
    highlights: ['Location aware', 'Prompt enrichment', 'Fast response'],
    configFields: [
      { key: 'n8n_webhook_url', label: 'n8n Webhook URL', placeholder: 'Leave empty for Docker-internal default' }
    ]
  },
  {
    id: 'email-context',
    name: 'Email Context Agent',
    description: 'Searches connected email to find relevant messages and summarizes them for added context.',
    icon: 'fas fa-envelope-open-text',
    color: '#a78bfa',
    provider: 'internal',
    category: 'Prompt Agent',
    triggerLabel: 'Runs during chat prompts',
    runtimeLabel: 'Google workflow',
    highlights: ['Mailbox context', 'Configurable model', 'Personalized summaries'],
    configFields: []
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar Agent',
    description: 'Checks availability and schedules calendar events with connected Google Calendar.',
    icon: 'fas fa-calendar-check',
    color: '#34d399',
    provider: 'internal',
    category: 'Prompt Agent',
    triggerLabel: 'Runs during chat prompts',
    runtimeLabel: 'Google workflow',
    highlights: ['Availability lookup', 'Event scheduling', 'Calendar context'],
    configFields: []
  },
  {
    id: 'pricing-sync',
    name: 'LLM Pricing Sync',
    description: 'Scrapes provider pricing pages and updates model cost references.',
    icon: 'fas fa-tags',
    color: '#f59e0b',
    provider: 'internal',
    category: 'Maintenance Agent',
    triggerLabel: 'Manual run',
    runtimeLabel: 'Internal scraper',
    highlights: ['Pricing metadata', 'Reference refresh', 'Admin tooling'],
    manualOnly: true
  },
  {
    id: 'claude-agent',
    name: 'Claude Code Agent',
    description: 'Helps the user plan software-related tasks.',
    icon: 'fas fa-code',
    color: '#800080',
    provider: 'internal',
    category: 'Prompt Agent',
    triggerLabel: 'Runs during chat prompts',
    runtimeLabel: 'Claude workflow',
    highlights: ['Software assistant', 'Architecture helper', 'Task planning'],
    configFields: [
      {
        key: 'model',
        label: 'Anthropic Model',
        type: 'select'
      },
      {
        key: 'api_key',
        label: 'Anthropic API Key',
        placeholder: 'Stored securely'
      }
    ],
    manualOnly: true
  }
];

const buildDefaultAgents = () =>
  DEFAULT_AGENTS.map((definition) => {
    const fields = definition.configFields || [];
    const baseConfig = Object.fromEntries(fields.map((field) => [field.key, '']));
    const config = baseConfig;

    return {
      ...definition,
      enabled: false,
      config
    };
  });

// The Email and Calendar agents share a single UI toggle. Their persisted
// `enabled` flags must move together — otherwise the toggle can read ON
// (because either agent is enabled) while only one is actually wired up
// in the agentic supervisor.
const syncSharedGoogleEnabled = (agentList) => {
  const sharedEnabled = agentList.some(
    (agent) => SHARED_GOOGLE_AGENT_IDS.includes(agent.id) && agent.enabled
  );
  return agentList.map((agent) =>
    SHARED_GOOGLE_AGENT_IDS.includes(agent.id)
      ? { ...agent, enabled: sharedEnabled }
      : agent
  );
};

function formatTimestamp(value) {
  if (!value) return 'Never';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getPromptAgentState(agent, agenticMode) {
  if (agent.manualOnly) {
    if (!agenticMode) {
      return {
        label: 'Stopped',
        tone: 'inactive',
        detail: 'Blocked by the master Agentic Mode switch.'
      };
    }
    return {
      label: 'On demand',
      tone: 'manual',
      detail: 'Runs only when an operator starts it.'
    };
  }

  if (agent.enabled && agenticMode) {
    return {
      label: 'Live',
      tone: 'active',
      detail: 'Enabled and participating in prompt enrichment right now.'
    };
  }

  if (agent.enabled && !agenticMode) {
    return {
      label: 'Stopped',
      tone: 'inactive',
      detail: 'Blocked by the master Agentic Mode switch.'
    };
  }

  if (agent.enabled) {
    return {
      label: 'Armed',
      tone: 'ready',
      detail: 'Saved as enabled and ready for the next time Agentic Mode is turned on.'
    };
  }

  return {
    label: 'Off',
    tone: 'inactive',
    detail: 'Disabled for prompt enrichment.'
  };
}

function getSlackState(slackStatus, agenticMode) {
  if (!agenticMode) {
    if (slackStatus.enabled || slackStatus.handler_running) {
      return {
        label: 'Stopped',
        tone: 'inactive',
        detail: 'Blocked by the master Agentic Mode switch.'
      };
    }

    if (slackStatus.api_authenticated) {
      return {
        label: 'Ready',
        tone: 'ready',
        detail: 'Tokens are valid and the connector can be started once Agentic Mode is on.'
      };
    }

    return {
      label: 'Attention',
      tone: 'inactive',
      detail: slackStatus.error || 'Slack is not configured yet.'
    };
  }

  if (!slackStatus.enabled) {
    return {
      label: 'Off',
      tone: 'inactive',
      detail: 'Slack agent is disabled. Enable it to accept Slack commands.'
    };
  }

  if (slackStatus.handler_running) {
    return {
      label: 'Connected',
      tone: 'active',
      detail: 'Listening for Slack DMs and mentions over Socket Mode.'
    };
  }

  if (slackStatus.api_authenticated) {
    return {
      label: 'Armed',
      tone: 'ready',
      detail: 'Slack is enabled and will connect when the runtime starts.'
    };
  }

  return {
    label: 'Attention',
    tone: 'inactive',
    detail: slackStatus.error || 'Slack is not configured yet.'
  };
}

function getSlackStartupLabel(startupEnabled) {
  if (startupEnabled === true) {
    return 'Enabled on startup via env';
  }

  return 'Disabled on startup via env';
}

export default function AgentsManagement() {
  const [agenticMode, setAgenticMode] = useState(false);
  const [agents, setAgents] = useState(buildDefaultAgents);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [syncingPricing, setSyncingPricing] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);
  const [showSlackModal, setShowSlackModal] = useState(false);
  const [slackStatus, setSlackStatus] = useState(DEFAULT_SLACK_STATUS);
  const [slackLoading, setSlackLoading] = useState(true);
  const [slackToggling, setSlackToggling] = useState(false);
  const [anthropicModels, setAnthropicModels] = useState([]);

  const refreshSlackStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setSlackLoading(true);
    }

    try {
      const status = await apiClient.getSlackStatus();
      setSlackStatus({ ...DEFAULT_SLACK_STATUS, ...status });
    } catch (error) {
      console.error('Failed to load Slack status:', error);
      setSlackStatus((previous) => ({
        ...previous,
        error: error.message || 'Unable to reach the Slack status endpoint.'
      }));
    } finally {
      if (!silent) {
        setSlackLoading(false);
      }
    }
  };

  const waitForSlackStopped = async (attempts = 10, delayMs = 300) => {
    let latest = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      latest = await apiClient.getSlackStatus();
      setSlackStatus({ ...DEFAULT_SLACK_STATUS, ...latest });

      if (!latest.enabled && !latest.handler_running) {
        return latest;
      }

      if (attempt < attempts - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }
    }

    return latest;
  };

  useEffect(() => {
    refreshSlackStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('agenticModeChanged', { detail: { enabled: agenticMode } }));
  }, [agenticMode]);

  useEffect(() => {
    const loadConfigFromBackend = async () => {
      try {
        const [config, googleStatus, anthropicModelResponse] = await Promise.all([
          apiClient.getAgentConfig(),
          apiClient.getGoogleStatus().catch(() => ({ connected: false })),
          apiClient.getModels('anthropic').catch(() => null)
        ]);
        setGoogleConnected(Boolean(googleStatus?.connected));
        if (anthropicModelResponse?.models) {
          setAnthropicModels(
            anthropicModelResponse.models.map((model) => ({
              value: model.id,
              label: model.name
            }))
          );
        }
        if (!config) return;

        const backendAgents = config.agents || {};
        const slackAgent = backendAgents[SLACK_AGENT_ID] || {};
        const sharedBackendConfig =
          backendAgents[EMAIL_AGENT_ID]?.config ||
          backendAgents[GOOGLE_CALENDAR_AGENT_ID]?.config ||
          {};
        const sharedConfig = { ...sharedBackendConfig };
        const hydratedAgents = DEFAULT_AGENTS.map((definition) => ({
          ...definition,
          enabled: backendAgents[definition.id]?.enabled ?? false,
          lastRun: backendAgents[definition.id]?.lastRun,
          config: (() => {
            const baseConfig = Object.fromEntries(
              (definition.configFields || []).map((field) => [field.key, ''])
            );
            const mergedConfig = SHARED_GOOGLE_AGENT_IDS.includes(definition.id)
              ? { ...baseConfig, ...sharedConfig }
              : { ...baseConfig, ...(backendAgents[definition.id]?.config || {}) };
            return mergedConfig;
          })()
        }));

        setAgenticMode(Boolean(config.agenticMode));
        setAgents(syncSharedGoogleEnabled(hydratedAgents));
        setSlackStatus((previous) => ({
          ...previous,
          enabled: Boolean(slackAgent.enabled)
        }));
      } catch (error) {
        console.error('Failed to load agent config from backend:', error);
      }
    };

    loadConfigFromBackend();
  }, []);

  const saveConfigToBackend = async (newMode, newAgents) => {
    newAgents = syncSharedGoogleEnabled(newAgents);
    const sharedAgentConfig = newAgents.find((a) => a.id === EMAIL_AGENT_ID)?.config
      || newAgents.find((a) => a.id === GOOGLE_CALENDAR_AGENT_ID)?.config
      || {};
    const normalizedSharedConfig = { ...sharedAgentConfig };
    const googleEnabled = Boolean(
      newAgents.find((a) => a.id === GOOGLE_CALENDAR_AGENT_ID)?.enabled
        || newAgents.find((a) => a.id === EMAIL_AGENT_ID)?.enabled
    );
    if (googleEnabled) {
      normalizedSharedConfig.timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } else {
      delete normalizedSharedConfig.timezone;
    }
    const payload = {
      agenticMode: newMode,
      agents: Object.fromEntries(
        newAgents.map((agent) => [
          agent.id,
          {
            enabled: Boolean(agent.enabled),
            config: SHARED_GOOGLE_AGENT_IDS.includes(agent.id)
              ? normalizedSharedConfig
              : (agent.config || {}),
            ...(agent.lastRun ? { lastRun: agent.lastRun } : {})
          }
        ])
      ),
      ...(googleEnabled && normalizedSharedConfig.timezone
        ? { timezone: normalizedSharedConfig.timezone }
        : {})
    };

    await apiClient.saveAgentConfig(payload);
  };

  const handleModeToggle = async (newMode) => {
    const previous = agenticMode;
    setAgenticMode(newMode);
    try {
      await saveConfigToBackend(newMode, agents);
      await refreshSlackStatus({ silent: true });
    } catch (error) {
      console.error('Failed to save agentic mode:', error);
      setAgenticMode(previous);
    }
  };

  const toggleAgent = async (agentId) => {
    const newAgents = agents.map((agent) =>
      agent.id === agentId ? { ...agent, enabled: !agent.enabled } : agent
    );
    setAgents(newAgents);
    try {
      await saveConfigToBackend(agenticMode, newAgents);
    } catch (error) {
      console.error('Failed to save agent config:', error);
    }
  };

  const toggleGoogleAgents = async () => {
    const nextEnabled = !(emailAgent?.enabled || calendarAgent?.enabled);
    const newAgents = agents.map((a) =>
      SHARED_GOOGLE_AGENT_IDS.includes(a.id) ? { ...a, enabled: nextEnabled } : a
    );
    setAgents(newAgents);
    try {
      await saveConfigToBackend(agenticMode, newAgents);
    } catch (error) {
      console.error('Failed to save google agent config:', error);
    }
  };

  const updateAgentConfig = async (agentId, key, value) => {
    const newAgents = agents.map((a) => {
      if (SHARED_GOOGLE_AGENT_IDS.includes(agentId) && SHARED_GOOGLE_AGENT_IDS.includes(a.id)) {
        return { ...a, config: { ...a.config, [key]: value } };
      }
      return a.id === agentId ? { ...a, config: { ...a.config, [key]: value } } : a;
    });
    setAgents(newAgents);
    try {
      await saveConfigToBackend(agenticMode, newAgents);
    } catch (error) {
      console.error('Failed to save agent config field:', error);
    }
  };

  const runPricingSyncAgent = async () => {
    setSyncingPricing(true);
    try {
      await apiClient.triggerPricingSync();
    } catch (error) {
      console.error('Failed to trigger pricing sync:', error);
    } finally {
      setSyncingPricing(false);
    }
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const redirectUri = `${window.location.origin}/oauth/google/callback`;
      const response = await apiClient.getGoogleAuthUrl(redirectUri);
      if (response?.url) {
        window.location.href = response.url;
      } else {
        console.error('Google auth URL missing from response:', response);
      }
    } catch (error) {
      console.error('Failed to start Google OAuth flow:', error);
    } finally {
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google for this account? You can reconnect it later.')) {
      return;
    }

    setDisconnectingGoogle(true);
    try {
      await apiClient.disconnectGoogle();
      setGoogleConnected(false);
      toastService.success('Google disconnected.', 3000);
    } catch (error) {
      console.error('Failed to disconnect Google:', error);
      toastService.error('Failed to disconnect Google.', 4000);
    } finally {
      setDisconnectingGoogle(false);
    }
  };

  const emailAgent = agents.find((a) => a.id === EMAIL_AGENT_ID);
  const calendarAgent = agents.find((a) => a.id === GOOGLE_CALENDAR_AGENT_ID);
  const googleEnabled = Boolean(emailAgent?.enabled || calendarAgent?.enabled);
  const nonGoogleAgents = agents.filter(
    (agent) => !SHARED_GOOGLE_AGENT_IDS.includes(agent.id) && agent.id !== 'pricing-sync'
  );
  const pricingAgent = agents.find((agent) => agent.id === 'pricing-sync');

  const renderAgentCard = (agent) => (
    <div
      key={agent.id}
      className={`setting-card agent-card${!agenticMode && agent.id !== 'pricing-sync' ? ' agent-card--off' : ''}`}
    >
      <div className="setting-row">
        <div className="agent-card-left">
          <div className="agent-icon" style={{ '--agent-color': agent.color }}>
            <i className={agent.icon}></i>
          </div>
          <div className="setting-info">
            <span className="setting-label">
              {agent.name}
              <span className="agent-badge">{agent.provider}</span>
            </span>
            {agent.description && <p className="setting-desc">{agent.description}</p>}

            {agent.id === 'pricing-sync' && agent.lastRun && (
              <div className="agent-metadata">
                <i className="far fa-clock"></i>
                Last synced {new Date(agent.lastRun).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </div>
        </div>
        <div className="agent-card-controls">
          {agent.id === 'pricing-sync' ? (
            <Button
              variant="secondary"
              size="sm"
              icon={syncingPricing ? 'fas fa-spinner fa-spin' : 'fas fa-play'}
              onClick={runPricingSyncAgent}
              disabled={syncingPricing}
              className="agent-run-btn"
            >
              {syncingPricing ? 'Running...' : 'Run now'}
            </Button>
          ) : (
            <>
              {agent.configFields && agent.configFields.length > 0 && (
                <button
                  className="agent-config-btn"
                  onClick={() =>
                    setExpandedAgent(expandedAgent === agent.id ? null : agent.id)
                  }
                  disabled={!agenticMode}
                >
                  <i className={`fas fa-chevron-${expandedAgent === agent.id ? 'up' : 'down'}`}></i>
                </button>
              )}

              <ToggleSwitch
                checked={agent.enabled}
                onChange={() => toggleAgent(agent.id)}
                disabled={!agenticMode}
                size="sm"
              />
            </>
          )}
        </div>
      </div>

      {expandedAgent === agent.id && agent.configFields && (
        <div className="agent-config-panel">
          {agent.configFields.map(field => {
            const value = agent.config?.[field.key] || '';
            const isSelect = field.type === 'select';
            const options = field.key === 'model' && agent.id === 'claude-agent'
              ? anthropicModels
              : field.options;
            return (
              <div key={field.key} className="agent-config-field">
                <label>{field.label}</label>
                {isSelect ? (
                  <select
                    value={value}
                    onChange={(e) => updateAgentConfig(agent.id, field.key, e.target.value)}
                  >
                    <option value="">Select a model</option>
                    {(options || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.key === 'api_key' ? 'password' : 'text'}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={(e) => updateAgentConfig(agent.id, field.key, e.target.value)}
                  />
                )}
              </div>
            );
          })}
          {agent.configHint && (
            <p className="agent-config-hint">
              <i className="fas fa-info-circle"></i>
              {agent.configHint}
            </p>
          )}
        </div>
      )}
    </div>
  );

  const handleSlackToggle = async (value) => {
    if (!agenticMode || slackToggling || slackLoading) {
      return;
    }

    const previous = { ...slackStatus };
    setSlackToggling(true);
    setSlackStatus((current) => ({
      ...current,
      enabled: value,
      error: null
    }));

    try {
      const response = value
        ? await apiClient.startSlackHandler()
        : await apiClient.stopSlackHandler();

      setSlackStatus((current) => ({
        ...current,
        enabled: response.enabled ?? value,
        handler_running: response.running ?? current.handler_running,
        error: null
      }));

      if (value) {
        await refreshSlackStatus({ silent: true });
      } else {
        await waitForSlackStopped();
      }
    } catch (error) {
      console.error('Failed to toggle Slack handler:', error);
      setSlackStatus(previous);
    } finally {
      setSlackToggling(false);
    }
  };

  const slackCardState = getSlackState(slackStatus, agenticMode);
  const slackStatusText = slackLoading ? 'Checking connector state...' : slackCardState.detail;

  return (
    <div className="page-container agents-management">
      <div className="page-content">
        <PageHeader
          title="Agents"
          description="Configure agents that run alongside your prompts to enrich responses with real-time data."
          icon="fas fa-robot"
          accentColor="purple"
        />

        <div className="settings-list">
          <div className="setting-card">
            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-label">Agentic Mode</span>
                <p className="setting-desc">
                  When enabled, active agents are triggered on every prompt to enrich responses with real-time data.
                  Individual agents can be toggled independently below.
                </p>
              </div>
              <ToggleSwitch checked={agenticMode} onChange={handleModeToggle} />
            </div>
          </div>
          {/* Agent cards */}
          {nonGoogleAgents.map(renderAgentCard)}

          <div className={`setting-card agent-card${!agenticMode ? ' agent-card--off' : ''}`}>
            <div className="setting-row">
              <div className="agent-card-left">
                <div className="agent-icon" style={{ '--agent-color': '#34d399' }}>
                  <i className="fas fa-envelope-open-text"></i>
                </div>
                <div className="setting-info">
                  <span className="setting-label">
                    Gmail and Google Calendar
                    <span className="agent-badge">internal</span>
                  </span>
                  <p className="setting-desc">
                    Configure shared Google settings for the Gmail and Google Calendar agents.
                  </p>
                </div>
              </div>
              <div className="agent-card-controls">
                <button
                  className="agent-config-btn"
                  onClick={() =>
                    setExpandedAgent(expandedAgent === EMAIL_AGENT_ID ? null : EMAIL_AGENT_ID)
                  }
                  disabled={!agenticMode}
                  type="button"
                >
                  <i className={`fas fa-chevron-${expandedAgent === EMAIL_AGENT_ID ? 'up' : 'down'}`}></i>
                </button>
                <ToggleSwitch
                  checked={googleEnabled}
                  onChange={toggleGoogleAgents}
                  disabled={!agenticMode || (!emailAgent && !calendarAgent)}
                  size="sm"
                />
              </div>
            </div>

            {expandedAgent === EMAIL_AGENT_ID && (
              <div className="agent-config-panel">
                <div className="agent-config-field">
                  <label>{googleConnected ? 'Google connected' : 'Google disconnected'}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {!googleConnected ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleConnectGoogle}
                        disabled={!agenticMode || connectingGoogle}
                        icon={connectingGoogle ? 'fas fa-spinner fa-spin' : 'fab fa-google'}
                      >
                        {connectingGoogle ? 'Connecting...' : 'Connect Google'}
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleDisconnectGoogle}
                        disabled={!agenticMode || disconnectingGoogle}
                        icon={disconnectingGoogle ? 'fas fa-spinner fa-spin' : 'fas fa-unlink'}
                      >
                        {disconnectingGoogle ? 'Disconnecting...' : 'Disconnect'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {pricingAgent && renderAgentCard(pricingAgent)}

        {/* Agent cards */}
          <div className={`setting-card agent-card${!agenticMode ? ' agent-card--off' : ''}`}>
            <div className="setting-row">
              <div className="agent-card-left">
                <div className="agent-icon" style={{ '--agent-color': '#4ade80' }}>
                  <i className="fab fa-slack"></i>
                </div>
                <div className="setting-info">
                  <span className="setting-label">
                    Slack Bot
                    <span className="agent-badge">socket mode</span>
                  </span>
                  <p className="setting-desc">
                    Answers Slack DMs and @mentions in real time, and can pull personal context when the user has linked
                    their Slack account.
                  </p>
                  <div className="agent-metadata">
                    <i className={`fas ${slackStatus.handler_running ? 'fa-broadcast-tower' : 'fa-plug'}`}></i>
                    {slackStatusText}
                  </div>
                  {slackStatus.startup_enabled !== null && (
                    <div className="agent-metadata">
                      <i className="fas fa-rotate"></i>
                      Backend startup default: {getSlackStartupLabel(slackStatus.startup_enabled)}
                    </div>
                  )}
                </div>
              </div>
              <div className="agent-card-controls agent-card-controls--slack">
                <Button
                  variant="secondary"
                  size="sm"
                  icon="fas fa-sliders"
                  onClick={() => setShowSlackModal(true)}
                >
                  Configure
                </Button>
                <ToggleSwitch
                  checked={agenticMode && slackStatus.enabled}
                  onChange={handleSlackToggle}
                  disabled={!agenticMode || slackToggling || slackLoading}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlackModal
        isOpen={showSlackModal}
        masterEnabled={agenticMode}
        onClose={() => setShowSlackModal(false)}
        onSave={() => refreshSlackStatus({ silent: true })}
      />
    </div>
  );
}
